"use client";

import { useState } from "react";
import { ChevronDown, Eye } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { IntegrationInfoContent } from "./integration-info-content";
import { IntegrationInfoDialog } from "./integration-info-dialog";

interface IntegrationCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  connected?: boolean;
  defaultOpen?: boolean;
  integrationInfo?: IntegrationInfoContent;
  children: React.ReactNode;
}

export function IntegrationCard({
  icon,
  title,
  description,
  connected,
  defaultOpen = false,
  integrationInfo,
  children,
}: IntegrationCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [infoOpen, setInfoOpen] = useState(false);

  return (
    <>
      <Collapsible
        open={open}
        onOpenChange={setOpen}
        className="group rounded-lg border"
      >
        <CollapsibleTrigger className="flex w-full items-start gap-3 p-4 text-left hover:bg-muted/50">
          <div className="shrink-0">{icon}</div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium leading-none">{title}</p>
              {connected === true ? (
                <Badge
                  variant="outline"
                  className="border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950 dark:text-green-400"
                >
                  Connected
                </Badge>
              ) : null}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          </div>
          <div className="flex shrink-0 items-center gap-0.5 self-start">
            {integrationInfo ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setInfoOpen(true);
                }}
              >
                <Eye className="mr-1.5 h-3.5 w-3.5" />
                View integration
              </Button>
            ) : null}
            <ChevronDown
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform duration-200",
                open && "rotate-180"
              )}
            />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-4 border-t px-4 pb-4 pt-4">{children}</div>
        </CollapsibleContent>
      </Collapsible>

      {integrationInfo ? (
        <IntegrationInfoDialog
          open={infoOpen}
          onOpenChange={setInfoOpen}
          content={integrationInfo}
        />
      ) : null}
    </>
  );
}
